import { getCopyrightUrl, getImprintUrl, getPrivacyUrl } from '../../support/constants.ts';
import * as React from 'react';
import classnames from '../../core/utils/classnames.tsx';

const AuthPageFooter = ({ className, children, ...props }: React.ComponentPropsWithRef<'footer'>) => {
    const languageCode = navigator.language.split('-')[0] === 'de' ? 'de' : 'en';
    const currentYear = new Date().getFullYear();

    return (
        <footer
            className={classnames(
                'absolute bottom-0 left-0 right-0 p-8 flex justify-between text-sm pointer-events-none',
                className
            )}
            {...props}
        >
            <a
                href={getCopyrightUrl(languageCode)}
                className="pointer-events-auto text-content-brand-default hover:text-content-brand-hover"
            >
                &copy; {currentYear} workist &trade;
            </a>
            <span className="pointer-events-auto">
                <a
                    href={getImprintUrl(languageCode)}
                    className="mx-2.5 text-content-brand-default hover:text-content-brand-hover"
                >
                    Impressum
                </a>
                <a
                    href={getPrivacyUrl(languageCode)}
                    className="mx-2.5 text-content-brand-default hover:text-content-brand-hover"
                >
                    Datenschutz
                </a>
            </span>
        </footer>
    );
};

const AuthPageCard = ({ className, children, ...props }: React.ComponentPropsWithRef<'div'>) => {
    return (
        <div
            className={classnames(
                'w-full max-w-96 bg-surface-weak rounded-lg p-8 border border-line-strong border-solid flex flex-col gap-6',
                className
            )}
            {...props}
        >
            {children}
        </div>
    );
};

const AuthPageSuccess = ({ className, children, ...props }: React.ComponentPropsWithRef<'div'>) => {
    return (
        <div
            className={classnames(
                'text-content-success-strong bg-surface-success-weak text-sm px-4 py-2.5 rounded',
                className
            )}
            {...props}
        >
            {children}
        </div>
    );
};

const AuthPageError = ({ className, children, ...props }: React.ComponentPropsWithRef<'div'>) => {
    return (
        <div
            className={classnames(
                'text-content-danger-strong bg-surface-danger-weak text-sm px-4 py-2.5 rounded',
                className
            )}
            {...props}
        >
            {children}
        </div>
    );
};

const AuthPageTitle = ({ className, children, ...props }: React.ComponentPropsWithRef<'h1'>) => {
    return (
        <h1 className={classnames('text-xl font-semibold text-content-strong', className)} {...props}>
            {children}
        </h1>
    );
};

const AuthPageContent = ({ className, children, ...props }: React.ComponentPropsWithRef<'div'>) => {
    return (
        <div
            className={classnames(
                'flex flex-col gap-8 m-auto w-full max-w-[34rem] items-center py-12 px-4 sm:px-10',
                className
            )}
            {...props}
        >
            {children}
        </div>
    );
};

const AuthPage = ({ className, ...props }: React.ComponentPropsWithRef<'div'>) => {
    return (
        <div
            className={classnames(
                'w-full min-h-dvh flex items-center justify-center bg-surface-medium overflow-y-auto',
                className
            )}
            {...props}
        />
    );
};

export default Object.assign(AuthPage, {
    Content: AuthPageContent,
    Card: AuthPageCard,
    Title: AuthPageTitle,
    Error: AuthPageError,
    Success: AuthPageSuccess,
    Footer: AuthPageFooter,
});
